import * as Sentry from "@sentry/react";

import { andonAuthClient, qubitAuthClient } from "./login.slice";
import { AuthMethod } from "./login.slice.interfaces";

export type AuthLogoutContext = {
  authMethod: AuthMethod | null;
  customLoginPath: string | undefined;
  loginPath: string;
};

export const isNonFederatedAuth = (
  authMethod: AuthLogoutContext["authMethod"]
): boolean => authMethod === "BEV_OPERATOR" || authMethod === "BEV_QUBIT";

export async function performAuthLogout(
  ctx: AuthLogoutContext
): Promise<boolean> {
  const { authMethod, customLoginPath, loginPath } = ctx;

  try {
    Sentry.addBreadcrumb({
      message: "logout: federated-auth-logout",
      level: "info",
      data: { authMethod: authMethod ?? "none" }
    });
  } catch {
    void 0;
  }

  const authClient =
    authMethod === "QUBIT" || authMethod === "E2E"
      ? qubitAuthClient
      : authMethod === "ANDON"
        ? andonAuthClient
        : null;

  if (!authClient) {
    return true;
  }

  const logoutParams = customLoginPath
    ? { returnTo: customLoginPath }
    : {
        federated: true,
        returnTo: `${window.location.origin}${loginPath}`
      };

  try {
    await authClient.logout({ logoutParams });
    return true;
  } catch (err) {
    Sentry.captureException(err, {
      tags: { feature: "auth-logout" },
      extra: { authMethod }
    });
    return false;
  }
}

export function reportFederatedLogoutFailure(ctx: AuthLogoutContext): void {
  Sentry.captureMessage(
    "logout: federated auth logout failed; showing retry dialog",
    {
      level: "warning",
      tags: { feature: "auth-logout" },
      extra: {
        authMethod: ctx.authMethod ?? "none",
        loginPath: ctx.loginPath,
        customLoginPath: ctx.customLoginPath ?? null
      }
    }
  );
}

export function reportSpaLoginFallback(ctx: AuthLogoutContext): void {
  Sentry.captureMessage(
    "logout: user chose SPA login fallback after federated logout failed",
    {
      level: "warning",
      tags: { feature: "auth-logout-fallback" },
      extra: {
        authMethod: ctx.authMethod ?? "none",
        loginPath: ctx.loginPath
      }
    }
  );
}
